import type { QueryClient } from "@tanstack/react-query";
import type {
  SendVerificationCodeResult,
  ValidateVerificationCodeResult,
} from "@shared/schemas/email-verification.schemas";
import type { UserIsolationStatus } from "@shared/schemas/user-isolation.schemas";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import { createAccountApi } from "./account.api";
import type { AxiosInstance } from "axios";
import { MyEventViewUnionSchema } from "@shared/schemas/event-views.schemas";
import type { MyEventViewUnion } from "@shared/schemas/event-views.schemas";

const ACCOUNT_QUERY_KEYS = {
  eventsAsParticipant: ["user-events", "as-participant"] as const,
  isolationStatus: ["user-isolation-status"] as const,
} as const;

interface GetIsolationStatusOptions {
  readonly enabled?: boolean | undefined;
}

export class AccountHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createAccountApi(apiClient);
  }

  createHooks() {
    return {
      useGetUserEventsAsParticipant: () =>
        this.query<readonly unknown[], readonly MyEventViewUnion[]>({
          key: ACCOUNT_QUERY_KEYS.eventsAsParticipant,
          fn: () => this.api.getUserEventsAsParticipant(),
          parse: (dtos) =>
            dtos
              .map((dto) => MyEventViewUnionSchema.parse(dto))
              .filter((event) => event.$roles.includes("event:participant")),
        }),

      useGetIsolationStatus: (options: GetIsolationStatusOptions = {}) =>
        this.query<UserIsolationStatus, UserIsolationStatus>({
          key: ACCOUNT_QUERY_KEYS.isolationStatus,
          fn: () => this.api.getIsolationStatus(),
          parse: (data) => data,
          ...(options.enabled !== undefined && { enabled: options.enabled }),
        }),

      useSendVerificationEmail: () =>
        this.mutation<
          SendVerificationCodeResult,
          SendVerificationCodeResult,
          void
        >({
          fn: () => this.api.sendVerificationEmail(),
          parse: (data) => data,
        }),

      useValidateVerificationCode: () =>
        this.mutation<
          ValidateVerificationCodeResult,
          ValidateVerificationCodeResult,
          string
        >({
          fn: (code) => this.api.validateVerificationCode(code),
          parse: (data) => data,
        }),
    };
  }
}
